// /src/ui/components/PartIcon.tsx

import React, { useMemo } from 'react';
import { findPartDef } from '@/engine/partsCatalog';

type PartIconProps = {
  name: string;
  partFile: string;
  colorHex: string;
  className?: string;
};

type PartDims = {
  w: number;
  d: number;
  h: number; // in "plate" units
  studs: boolean;
};

const ICON_CACHE = new Map<string, string>();

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

function normalizeHex(raw: string): string {
  const s = String(raw ?? '').trim();
  if (/^#[0-9a-f]{6}$/i.test(s)) return s.toLowerCase();
  if (/^#[0-9a-f]{3}$/i.test(s)) {
    const r = s[1];
    const g = s[2];
    const b = s[3];
    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
  }
  return '#c0c0c0';
}

function adjustHex(hex: string, amount: number): string {
  const h = normalizeHex(hex);
  const r = parseInt(h.slice(1, 3), 16);
  const g = parseInt(h.slice(3, 5), 16);
  const b = parseInt(h.slice(5, 7), 16);

  const next = (v: number) => {
    if (amount >= 0) return Math.round(v + (255 - v) * amount);
    return Math.round(v * (1 + amount));
  };

  const rr = clamp(next(r), 0, 255).toString(16).padStart(2, '0');
  const gg = clamp(next(g), 0, 255).toString(16).padStart(2, '0');
  const bb = clamp(next(b), 0, 255).toString(16).padStart(2, '0');
  return `#${rr}${gg}${bb}`;
}

function inferDims(name: string, partFile: string): PartDims {
  const fromCatalog = findPartDef(partFile);
  if (fromCatalog) {
    return {
      w: clamp(fromCatalog.studs.w, 1, 8),
      d: clamp(fromCatalog.studs.d, 1, 8),
      h: clamp(fromCatalog.studs.plates, 1, 24),
      studs: !/tile/i.test(fromCatalog.name),
    };
  }

  const n = String(name ?? '').toLowerCase();
  const m = n.match(/(\d+)\s*x\s*(\d+)(?:\s*x\s*(\d+))?/i);

  let w = m ? Math.max(1, Number(m[1])) : 1;
  let d = m ? Math.max(1, Number(m[2])) : 1;
  let h = n.includes('tile') || n.includes('plate') ? 1 : 3;

  if (m && m[3]) {
    const third = Math.max(1, Number(m[3]));
    h = n.includes('brick') ? third * 3 : third;
  }

  if (/(axle|bar|hose|whip|antenna|rod|pin)\b/i.test(n)) {
    w = 1;
    d = 1;
    h = Math.max(h, 10);
  }

  return {
    w: clamp(w, 1, 8),
    d: clamp(d, 1, 8),
    h: clamp(h, 1, 30),
    studs: !/tile|axle|bar|hose|whip|antenna|rod|pin/i.test(n),
  };
}

function makeIconDataUrl(name: string, partFile: string, colorHex: string): string {
  const color = normalizeHex(colorHex);
  const cacheKey = `${String(name)}|${String(partFile)}|${color}`;
  const cached = ICON_CACHE.get(cacheKey);
  if (cached) return cached;

  const dims = inferDims(name, partFile);
  const w = dims.w;
  const d = dims.d;
  const h = dims.h;

  const baseUx = 6;
  const baseUz = 3.6;
  const baseUy = 1.9;
  const rawW = (w + d) * baseUx;
  const rawH = (w + d) * baseUz + h * baseUy;
  const scale = Math.min(1.4, 54 / rawW, 35 / rawH);

  const ux = baseUx * scale;
  const uz = baseUz * scale;
  const uy = baseUy * scale;

  const proj = (x: number, y: number, z: number) => ({
    x: (x - z) * ux,
    y: (x + z) * uz - y * uy,
  });

  const t00 = proj(0, h, 0);
  const t10 = proj(w, h, 0);
  const t11 = proj(w, h, d);
  const t01 = proj(0, h, d);
  const b10 = proj(w, 0, 0);
  const b11 = proj(w, 0, d);
  const b01 = proj(0, 0, d);

  const all = [t00, t10, t11, t01, b10, b11, b01];
  const minX = Math.min(...all.map((p) => p.x));
  const maxX = Math.max(...all.map((p) => p.x));
  const maxY = Math.max(...all.map((p) => p.y));

  const tx = 32 - (minX + maxX) * 0.5;
  const ty = 43 - maxY;

  const pt = (p: { x: number; y: number }) =>
    `${(p.x + tx).toFixed(2)},${(p.y + ty).toFixed(2)}`;

  const poly = (points: Array<{ x: number; y: number }>) => points.map(pt).join(' ');

  const topColor = adjustHex(color, 0.24);
  const side1Color = adjustHex(color, -0.04);
  const side2Color = adjustHex(color, -0.2);
  const lineColor = adjustHex(color, -0.38);

  const studs: string[] = [];
  const studCount = w * d;
  const showStuds = dims.studs && studCount > 0;

  if (showStuds) {
    const targets: Array<{ x: number; z: number }> = [];
    if (studCount <= 12) {
      for (let ix = 0; ix < w; ix++) {
        for (let iz = 0; iz < d; iz++) targets.push({ x: ix, z: iz });
      }
    } else {
      targets.push({ x: 0, z: 0 });
      targets.push({ x: w - 1, z: 0 });
      targets.push({ x: 0, z: d - 1 });
      targets.push({ x: w - 1, z: d - 1 });
    }

    const rx = Math.max(1.2, 1.9 * scale);
    const ry = Math.max(0.8, 1.1 * scale);
    const studFill = adjustHex(topColor, 0.12);
    const studStroke = adjustHex(color, -0.28);

    for (const s of targets) {
      const c = proj(s.x + 0.5, h, s.z + 0.5);
      studs.push(
        `<ellipse cx="${(c.x + tx).toFixed(2)}" cy="${(c.y + ty).toFixed(2)}" ` +
        `rx="${rx.toFixed(2)}" ry="${ry.toFixed(2)}" fill="${studFill}" stroke="${studStroke}" stroke-width="0.8"/>`
      );
    }
  }

  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 48">` +
    `<polygon points="${poly([t10, t11, b11, b10])}" fill="${side1Color}" stroke="${lineColor}" stroke-width="0.9"/>` +
    `<polygon points="${poly([t01, t11, b11, b01])}" fill="${side2Color}" stroke="${lineColor}" stroke-width="0.9"/>` +
    `<polygon points="${poly([t00, t10, t11, t01])}" fill="${topColor}" stroke="${lineColor}" stroke-width="0.9"/>` +
    studs.join('') +
    `</svg>`;

  const encoded = `data:image/svg+xml,${encodeURIComponent(svg)}`;
  ICON_CACHE.set(cacheKey, encoded);
  return encoded;
}

export default function PartIcon({ name, partFile, colorHex, className }: PartIconProps) {
  const src = useMemo(() => makeIconDataUrl(name, partFile, colorHex), [name, partFile, colorHex]);
  return <img className={className} src={src} alt="" draggable={false} />;
}

